import {
  doc, setDoc, updateDoc, getDoc, serverTimestamp, collection, addDoc, query, where, getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../config/firebase';
import { APPLICATION_STATUS, DOCUMENT_TYPES } from '../config/constants';

// ═══════════════ VALIDATE INVITATION LINK ═══════════════
export async function validateInvitationLink(linkId) {
  const linkRef = doc(db, 'invitationLinks', linkId);
  const linkDoc = await getDoc(linkRef);

  if (!linkDoc.exists()) {
    throw new Error('Invalid invitation link.');
  }

  const data = linkDoc.data();

  if (data.usedAt) {
    throw new Error('This invitation link has already been used.');
  }

  if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
    throw new Error('This invitation link has expired.');
  }

  return data;
}

// ═══════════════ MARK INVITATION AS USED ═══════════════
export async function markInvitationUsed(linkId, userId) {
  const linkRef = doc(db, 'invitationLinks', linkId);
  await updateDoc(linkRef, {
    usedAt: serverTimestamp(),
    usedBy: userId,
  });
}

// ═══════════════ SAVE APPLICATION PROGRESS ═══════════════
export async function saveApplicationProgress(userId, stepData, currentStep) {
  const appRef = doc(db, 'driverApplications', userId);
  const appDoc = await getDoc(appRef);

  const updateData = {
    [`step${currentStep}`]: stepData,
    currentStep,
    updatedAt: serverTimestamp(),
  };

  if (appDoc.exists()) {
    await updateDoc(appRef, updateData);
  } else {
    await setDoc(appRef, {
      userId,
      status: APPLICATION_STATUS.PENDING,
      currentStep,
      ...updateData,
      createdAt: serverTimestamp(),
    });
  }
}

// ═══════════════ UPLOAD DOCUMENT ═══════════════
export async function uploadDocument(userId, file, documentType, metadata = {}) {
  // Validate file
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    throw new Error('File size exceeds 10MB limit.');
  }

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('File type not supported. Use JPEG, PNG, WebP, or PDF.');
  }

  // Upload to Firebase Storage
  const timestamp = Date.now();
  const ext = file.name.split('.').pop();
  const storagePath = `drivers/${userId}/documents/${documentType}_${timestamp}.${ext}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      userId,
      documentType,
      uploadedAt: new Date().toISOString(),
    },
  });

  const downloadURL = await getDownloadURL(snapshot.ref);

  // Save document record in Firestore
  const docRecord = {
    driverId: userId,
    type: documentType,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
    storagePath,
    downloadURL,
    status: 'pending', // pending → approved / rejected by admin
    uploadedAt: serverTimestamp(),
    ...metadata, // expiryDate, documentNumber, etc.
  };

  const docRef = await addDoc(collection(db, 'driverDocuments'), docRecord);
  return { id: docRef.id, ...docRecord, downloadURL };
}

// ═══════════════ UPLOAD VEHICLE PHOTOS ═══════════════
export async function uploadVehiclePhoto(userId, file, photoType) {
  const maxSize = 10 * 1024 * 1024;
  if (file.size > maxSize) throw new Error('Photo exceeds 10MB limit.');

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) throw new Error('Only JPEG, PNG, or WebP photos accepted.');

  const timestamp = Date.now();
  const ext = file.name.split('.').pop();
  const storagePath = `drivers/${userId}/vehicle/${photoType}_${timestamp}.${ext}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, { contentType: file.type });
  const downloadURL = await getDownloadURL(snapshot.ref);

  return { storagePath, downloadURL, photoType };
}

// ═══════════════ CHECK PHONE AVAILABILITY ═══════════════
export async function checkPhoneAvailable(phone) {
  const fn = httpsCallable(functions, 'checkPhoneAvailable');
  const res = await fn({ phone });
  return res.data; // { available }
}

// ═══════════════ VERIFY ABN ═══════════════
export async function verifyABN(abn) {
  const verify = httpsCallable(functions, 'verifyABN');
  const result = await verify({ abn: abn.replace(/\s/g, '') });
  return result.data; // { valid, entityName, gstRegistered, abnStatus }
}

// ═══════════════ SAVE VEHICLE DETAILS ═══════════════
export async function saveVehicleDetails(userId, vehicleData) {
  const vehicleRef = doc(collection(db, 'vehicles'));
  await setDoc(vehicleRef, {
    driverId: userId,
    ...vehicleData,
    status: 'pending', // Pending admin approval
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return vehicleRef.id;
}

// ═══════════════ SAVE SERVICE PREFERENCES ═══════════════
export async function saveServicePreferences(userId, preferences) {
  // preferences = { zones: [...], availability: {...}, childSeatEquipped: bool }
  await updateDoc(doc(db, 'driverApplications', userId), {
    step8: preferences,
    currentStep: 8,
    updatedAt: serverTimestamp(),
  });
}

// ═══════════════ SUBMIT APPLICATION ═══════════════
export async function submitApplication(userId) {
  const appRef = doc(db, 'driverApplications', userId);

  await updateDoc(appRef, {
    status: APPLICATION_STATUS.DOCUMENTS_SUBMITTED,
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Trigger Cloud Function to notify admins + assign reference number
  const notifyAdmin = httpsCallable(functions, 'notifyNewDriverApplication');
  await notifyAdmin({ userId });

  return true;
}

// ═══════════════ GET APPLICATION STATUS ═══════════════
export async function getApplicationStatus(userId) {
  const appRef = doc(db, 'driverApplications', userId);
  const appDoc = await getDoc(appRef);

  if (!appDoc.exists()) return null;
  return appDoc.data();
}



// ═══════════════ RE-UPLOAD A DOCUMENT (supersede prior versions) ═══════════════
// Used on resubmission: keeps the old document records for history but marks them
// superseded, and tags the new upload as the current version for the admin to review.
export async function reuploadDocument(userId, file, documentType, metadata = {}) {
  const q = query(
    collection(db, 'driverDocuments'),
    where('driverId', '==', userId),
    where('type', '==', documentType),
  );
  const snap = await getDocs(q);
  await Promise.all(
    snap.docs
      .filter((dDoc) => !dDoc.data().isSuperseded)
      .map((dDoc) => updateDoc(dDoc.ref, { isSuperseded: true, supersededAt: serverTimestamp() })),
  );
  return uploadDocument(userId, file, documentType, { ...metadata, isLatest: true, isResubmission: true });
}

// ═══════════════ RESUBMIT AFTER CHANGES REQUESTED ═══════════════
// Mirrors submitApplication, but for an existing account whose application was sent
// back. Updates any edited user-doc fields, flips the application back into the review
// queue, clears the change request, and re-notifies admins (no new reference number).
export async function resubmitApplication(userId, fieldUpdates = {}) {
  const cleaned = {};
  Object.keys(fieldUpdates || {}).forEach((k) => {
    if (fieldUpdates[k] !== undefined && fieldUpdates[k] !== null && String(fieldUpdates[k]).trim() !== '') {
      cleaned[k] = fieldUpdates[k];
    }
  });
  if (Object.keys(cleaned).length) {
    await updateDoc(doc(db, 'users', userId), { ...cleaned, updatedAt: serverTimestamp() });
  }
  await updateDoc(doc(db, 'driverApplications', userId), {
    status: APPLICATION_STATUS.DOCUMENTS_SUBMITTED,
    resubmittedAt: serverTimestamp(),
    changesRequested: null,
    changesRequestedFields: [],
    updatedAt: serverTimestamp(),
  });
  const notify = httpsCallable(functions, 'notifyNewDriverApplication');
  await notify({ userId, resubmission: true });
  return true;
}

// ═══════════════ SETUP STRIPE CONNECT ═══════════════
export async function createStripeConnectAccount(userId) {
  const createAccount = httpsCallable(functions, 'createStripeConnectAccount');
  const result = await createAccount({ userId });
  return result.data; // { accountId, onboardingUrl }
}